/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import React, { useContext, useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import produce from "immer";
import { LocationsContext } from ".";
import { useParams } from "react-router-dom";
import fetchHelper from "../../services/fetchHelper";

import savingSpinner from "../../assets/esign-ui-icons/saving-ico-white.svg";
import { addTimeToDateString } from "../../utils/inject";

const AddSchedule = ({ item, onSave }) => {
  const [editForm, setEditForm] = useState(false);
  const [data, setData] = useState(item || {});
  const [isLoading, setIsLoading] = useState(false);

  const onChange = (e) => {
    const element = e.target;
    const key = element.dataset.key;
    let value = element.value;
    setData((prev) =>
      produce(prev, (draft) => {
        draft[key] = value;
      })
    );
  };

  return (
    <div className="row rowStretch row-col-options">
      <div className="col col-s-100 col33">
        <label>
          <span>Schedule Name</span>
          <input
            type="text"
            value={data?.scheduleName}
            placeholder="Enter Schedule Name..."
            data-key="scheduleName"
            onChange={onChange}
            disabled={!editForm}
          />
        </label>
      </div>
      <div className="col col-s-50 col33 inCal-col">
        <label className="inCal">
          <span>Start Date</span>
        </label>
        <DatePicker
          disabled={!editForm}
          selected={(data?.startDate && new Date(addTimeToDateString(data?.startDate))) || ""}
          onChange={(date) => {
            setData((prev) =>
              produce(prev, (draft) => {
                draft["startDate"] = date.toStandardDate();
              })
            );
          }}
        />
      </div>
      <div className="col col-s-50 col33 colRemoteMonitorSett">
        <label className="inBtn">
          <span>&nbsp;</span>
          <ul>
            {editForm ? (
              <>
                <li>
                  <button
                    className="btn btnSuccess"
                    onClick={async () => {
                      setIsLoading(true);
                      await onSave(data);
                      setIsLoading(false);
                      setEditForm(false);
                    }}
                  >
                    {isLoading ? (
                      <div
                        id="results-loading-icon"
                        className="saving-white-ico icon-64"
                        style={{ width: 15, height: 15, marginRight: 3 }}
                      />
                    ) : null}
                    Save
                  </button>
                </li>
                <li>
                  <button className="btn btnNoTxt" onClick={(e) => setEditForm(false)}>
                    <i className="icon-64 close-flat-ico" style={{ width: 20, height: 20 }}></i>
                  </button>
                </li>
              </>
            ) : (
              <li>
                <button className="btn btnTealL" onClick={(e) => setEditForm(true)}>
                  Edit
                </button>
              </li>
            )}
          </ul>
        </label>
      </div>
    </div>
  );
};

const RemoteMonitoring = () => {
  const { i } = useParams();
  const { location } = useContext(LocationsContext);
  const [data, setData] = useState(location || {});

  const save = async (data) => {
    try {
      let d;
      if (data.id) d = await fetchHelper.put("/patientmanagement/rest/rmonitoringschedule/" + data.id, data);
      else d = await fetchHelper.post("/patientmanagement/rest/rmonitoringschedule/", data);
      setData((prev) =>
        produce(prev, (draft) => {
          const t = d.scheduleType === "THIRTYDAYS" ? "thirtyDaysSchedules" : "ninetyDaysSchedules";
          draft[t].pop();
          draft[t].push(d);
        })
      );
    } catch (err) {
      console.log(err);
    }
  };

  const addToSchedule = (type) => {
    setData((prev) =>
      produce(prev, (draft) => {
        const t = type === "THIRTYDAYS" ? "thirtyDaysSchedules" : "ninetyDaysSchedules";
        draft[t].push({ scheduleType: type, scheduleName: "", startDate: "", location: { id: i } });
      })
    );
  };

  return (
    <div className="tabContent remote-monitoring">
      <div className="content">
        <div className="row rowStretch">
          <div className="col col100">
            <label className="chk-option" style={{ display: "flex", alignItems: "center" }}>
              <input type="checkbox" className="chk" />
              <span className="chkmrk" style={{ width: "10px" }}></span>
              <span className="lblTxt" style={{ display: "inline" }}>
                Auto-Calibration Enabled
              </span>
            </label>
          </div>
        </div>
        <div className="row rowStretch">
          <div className="col col-m-100 col-l-100 col50 remoteMonitorSett-col">
            <div className="row rowStretch">
              <div className="col col60 grpHdr-btn">
                <div className="grpHdr">
                  <h3>30 Day Group Schedule</h3>
                </div>
              </div>
              <div className="col col40 grpHdr-btn btnAdd-to-schedule">
                <button className="btn btnTeal" onClick={() => addToSchedule("THIRTYDAYS")}>
                  Add to Schedule
                </button>
              </div>
            </div>

            {data?.thirtyDaysSchedules?.map((item, index) => (
              <AddSchedule key={index} onSave={save} item={item} />
            ))}
          </div>
          <div className="col col-m-100 col-l-100 col50 remoteMonitorSett-col">
            <div className="row rowStretch">
              <div className="col col60 grpHdr-btn">
                <div className="grpHdr">
                  <h3>90 Day Group Schedule</h3>
                </div>
              </div>
              <div className="col col40 grpHdr-btn btnAdd-to-schedule">
                <button className="btn btnTeal" onClick={() => addToSchedule("NINETYDAYS")}>
                  Add to Schedule
                </button>
              </div>
            </div>

            {data?.ninetyDaysSchedules?.map((item, index) => (
              <AddSchedule key={index} onSave={save} item={item} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RemoteMonitoring;
